import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { HelpEntry } from './helpEntries';
import { loadHelpEntries } from './loadHelpEntries';
import { readSeenHelpIds, writeSeenHelpIds } from './helpSeenStorage';

/**
 * Состояние «Подсказок и новостей» для шапки списка чатов.
 *
 * <p>Отдельным хуком, а не парой `useState` внутри экрана: список чатов и без того держит
 * полсотни состояний, а здесь своя связка «прочитанное на диске → счётчик непрочитанного →
 * пометить прочитанным», и она пригодится любому другому месту, откуда решат открыть тот же
 * ящик.
 *
 * <p>До первого чтения с диска ({@code ready}) непрочитанным считается ноль, а не «всё»: иначе
 * лампочка мигала бы у всех при каждом запуске приложения на те доли секунды, пока читается
 * хранилище, — и мигание перестало бы что-либо значить.
 *
 * <p>Тексты записей ({@link ./helpEntries}, ~30 КБ на четырёх языках) грузятся по требованию,
 * вместе с прочитанным: в основной веб-бандл они не входят, а лампочке до {@code ready} считать
 * всё равно нечего.
 */
type HelpCenterState = {
  entries: readonly HelpEntry[];
  /** Идентификаторы непрочитанных записей: ящику — для значка «Новое», лампочке — для счёта. */
  unreadIds: string[];
  unreadCount: number;
  /** Прочитано ли хранилище. Пока нет — счётчик заведомо нулевой и мигать нечему. */
  ready: boolean;
  /** Отметить всё прочитанным. Вызывается при открытии ящика — читать он и предлагает. */
  markAllRead: () => void;
};

type HelpEntriesModule = typeof import('./helpEntries');

export function useHelpCenter(): HelpCenterState {
  const [seenIds, setSeenIds] = useState<string[]>([]);
  const [help, setHelp] = useState<HelpEntriesModule | null>(null);
  const helpRef = useRef<HelpEntriesModule | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([loadHelpEntries(), readSeenHelpIds()]).then(([mod, stored]) => {
      if (cancelled) return;
      // Свежая установка (аудит 2026-10-04, п. 17): «что нового за сентябрь» новичку не
      // новость — для него всё новое. Помечаем такие записи прочитанными сразу, и лампочка
      // зовёт только к «С чего начать».
      const ids = stored ?? mod.initialSeenHelpIdsForNewcomer();
      if (stored === null) void writeSeenHelpIds(ids);
      helpRef.current = mod;
      setSeenIds(ids);
      setHelp(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const unreadIds = useMemo(() => (help ? help.unreadHelpEntryIds(seenIds) : []), [help, seenIds]);

  const markAllRead = useCallback(() => {
    const mod = helpRef.current;
    // Ящик открывают из уже показанного списка, а до загрузки записей в нём пусто — и
    // помечать прочитанным нечего.
    if (!mod) return;
    const ids = mod.allHelpEntryIds();
    setSeenIds(ids);
    // Записываем ровно текущий набор, а не объединение с прочитанным раньше: идентификаторы
    // выбывших записей больше ничего не прикрывают, и хранить их — значит копить мусор.
    void writeSeenHelpIds(ids);
  }, []);

  return {
    entries: help ? help.HELP_ENTRIES : [],
    unreadIds,
    unreadCount: unreadIds.length,
    ready: help !== null,
    markAllRead,
  };
}
